/**
 * Pure logic of the link screen: a model and the fabrics / elements it offers,
 * plus its mutual compatibilities and incompatibilities, edited as resolved
 * items and saved back as ids.
 */
import { getDmsProductLineLabel } from '@/shared/constants';
import { z } from 'zod';
import type { DmsItem } from '../api';
import { sortByKey } from '@/shared/lib/sort';
import { numberKey } from '../list';

/** The fields of a model, fabric or element the link screen reads; the rest is kept. */
export const linkDataSchema = z
  .object({
    order: z.unknown().nullish(),
    productLine: z.string().nullish(),
    model: z.string().nullish(),
    type: z.string().nullish(),
    name: z.string().nullish(),
    code: z.string().nullish(),
    options: z.unknown().nullish(),
    compatibilities: z.unknown().nullish(),
    incompatibilities: z.unknown().nullish(),
  })
  .loose();
export type LinkData = z.infer<typeof linkDataSchema>;
export type LinkItem = DmsItem<LinkData>;

export type ItemPair = [LinkItem, LinkItem];

/** The selected model with its relations resolved to items. */
export interface EditableModel {
  item: LinkItem;
  options: LinkItem[];
  compatibilities: ItemPair[];
  incompatibilities: ItemPair[];
}

export interface LinkCatalogue {
  items: LinkItem[];
  models: LinkItem[];
  /** Keyed by element type, in the order the types first appear. */
  elementsByType: Record<string, LinkItem[]>;
  allFabrics: LinkItem[];
}

/** Everything sorted by `data.order`, then split by type. */
export const buildCatalogue = (raw: readonly LinkItem[]): LinkCatalogue => {
  const items = sortByKey(raw, (item) => numberKey(item.data.order), false);
  const catalogue: LinkCatalogue = {
    items,
    models: [],
    elementsByType: {},
    allFabrics: [],
  };
  items.forEach((item) => {
    if (item.type === 'model') catalogue.models.push(item);
    else if (item.type === 'element') {
      const type = String(item.data.type);
      catalogue.elementsByType[type] ??= [];
      catalogue.elementsByType[type].push(item);
    } else if (item.type === 'fabric') catalogue.allFabrics.push(item);
  });
  return catalogue;
};

const resolvePairs = (pairs: unknown, items: readonly LinkItem[]): ItemPair[] =>
  (Array.isArray(pairs) ? pairs : []).flatMap((ids) => {
    const objs = (Array.isArray(ids) ? ids : [])
      .map((id) => items.find((item) => item.id === id))
      .filter((item): item is LinkItem => !!item);
    return objs.length === 2 ? [[objs[0]!, objs[1]!] as ItemPair] : [];
  });

/** Ids mapped to items, pairs dropped when incomplete. */
export const resolveModel = (
  model: LinkItem,
  items: readonly LinkItem[],
): EditableModel => ({
  item: model,
  options: (Array.isArray(model.data.options) ? model.data.options : [])
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is LinkItem => !!item),
  compatibilities: resolvePairs(model.data.compatibilities, items),
  incompatibilities: resolvePairs(model.data.incompatibilities, items),
});

/** The item with its relations back as ids. */
export const modelToDbData = (model: EditableModel): LinkItem => ({
  ...model.item,
  data: {
    ...model.item.data,
    options: model.options.map((opt) => opt.id),
    compatibilities: model.compatibilities.map((pair) => pair.map((o) => o.id)),
    incompatibilities: model.incompatibilities.map((pair) =>
      pair.map((o) => o.id),
    ),
  },
});

export const pageTitle = (model: EditableModel | null): string =>
  model
    ? `${getDmsProductLineLabel(String(model.item.data.productLine ?? '')) ?? 'undefined'} > ${model.item.data.model ?? ''}`
    : 'Please select a model';

export const modelHasItemOfType = (
  model: EditableModel | null,
  type: string,
  subtype?: string,
): boolean =>
  !!model &&
  model.options.some(
    (opt) => opt.type === type && (!subtype || opt.data.type === subtype),
  );

export const modelHasItem = (model: EditableModel, item: LinkItem): boolean =>
  model.options.some((opt) => opt.id === item.id);

/** The fabrics of the model, filtered by code. */
export const modelFabrics = (model: EditableModel, search = ''): LinkItem[] =>
  model.options.filter(
    (opt) =>
      opt.type === 'fabric' &&
      (!search ||
        String(opt.data.code ?? '')
          .toLowerCase()
          .includes(search)),
  );

/** Elements by type, filtered by name; searched types stay listed when empty. */
export const groupedModelElements = (
  model: EditableModel,
  searchElement: Record<string, string>,
): Record<string, LinkItem[]> => {
  const grouped: Record<string, LinkItem[]> = {};
  model.options.forEach((opt) => {
    if (opt.type !== 'element') return;
    const type = String(opt.data.type);
    const search = searchElement[type];
    if (
      search &&
      !String(opt.data.name ?? '')
        .toLowerCase()
        .includes(search)
    )
      return;
    grouped[type] ??= [];
    grouped[type].push(opt);
  });
  Object.keys(searchElement).forEach((type) => {
    grouped[type] ??= [];
  });
  return grouped;
};

export const unselectedItems = (
  model: EditableModel,
  candidates: readonly LinkItem[],
): LinkItem[] =>
  candidates.filter((item) => !model.options.some((opt) => opt.id === item.id));

export const incompLabel = (item: LinkItem): string =>
  item.type === 'fabric'
    ? `FABRIC > ${item.data.code}`
    : `${item.data.type} > ${item.data.name}`;

/**
 * The options of the model, minus those of the same
 * type as the item picked in the other field, sorted by label.
 */
export const pairOptions = (
  model: EditableModel,
  other: LinkItem | null,
): { label: string; value: LinkItem }[] =>
  sortByKey(
    model.options
      .filter((opt) => !other || opt.data.type !== other.data.type)
      .map((opt) => ({ label: incompLabel(opt), value: opt })),
    (opt) => opt.label,
    false,
  );

/** At least two distinct `data.type` among the options. */
export const displayMutualIncompatibilities = (
  model: EditableModel | null,
): boolean =>
  !!model && new Set(model.options.map((opt) => opt.data.type)).size >= 2;

export const noSpaces = (label: string): string => label.replace(/\s/g, '_');
